import { Box, makeStyles } from "@material-ui/core";
import { ChevronRight, InfoOutlined } from "@material-ui/icons";
import * as React from "react";
import * as $ from "jquery";
import { Colors } from "styles/Colors";
import Leaderboard from "common/components/Leaderboard/Leaderboard";
import UserManager from "common/userManager";
import TrophyService from "services/TrophyService";
import SeasonService from "services/SeasonService";
import UserCard from "common/components/UserCard";
import ContentHeader from "common/components/ContentHeader/ContentHeader";
import ModalHelper from "common/helpers/modalHelper";
import { toMonthNameDayAndYear } from "common/helpers/dateHelper";
import SeasonScoreModal from "./SeasonScoreModal";

const podiumPlaces = ["1st", "2nd", "3rd"];

const useStyles = makeStyles({
	root: {
		backgroundColor: Colors.panel,
		borderRadius: 12,
		padding: "5px 10px 10px",
		marginBottom: 12,
	},
	headerRow: {
		display: "flex",
		alignItems: "center",
		marginBottom: 5,
		"& h4": {
			marginBottom: 0,
		},
	},
	season: {
		marginLeft: "auto",
		fontSize: 12,
		color: Colors.primaryDarker,
	},
	seasonDot: {
		margin: "0 5px",
	},
	standing: {
		backgroundColor: Colors.panelLight,
		borderRadius: 12,
		padding: "9px 14px 10px",
		marginBottom: 14,
	},
	standingTop: {
		display: "flex",
		alignItems: "baseline",
	},
	standingRank: {
		fontSize: 22,
		fontWeight: "bold",
		lineHeight: "24px",
		color: Colors.pickstop,
	},
	standingField: {
		fontSize: 12,
		color: Colors.primaryDarker,
		marginLeft: 6,
	},
	standingBand: {
		marginLeft: 10,
		fontSize: 11,
		fontWeight: "bold",
		letterSpacing: 1,
		color: Colors.panel,
		backgroundColor: Colors.primaryDark,
		borderRadius: 4,
		padding: "2px 6px",
	},
	standingScore: {
		marginLeft: "auto",
		fontSize: 22,
		fontWeight: "bold",
		lineHeight: "24px",
		color: Colors.pickstop,
	},
	standingUnit: {
		fontSize: 11,
		color: Colors.primaryDarker,
		marginLeft: 4,
	},
	standingGap: {
		marginTop: 5,
		fontSize: 12,
	},
	gapSeparator: {
		color: Colors.panelLighterColor,
		margin: "0 7px",
	},
	podium: {
		display: "flex",
		gap: 6,
		marginBottom: 14,
	},
	tile: {
		flex: 1,
		minWidth: 0,
		backgroundColor: Colors.panelDark,
		border: "1px solid transparent",
		borderRadius: 12,
		padding: "8px 0 10px",
		textAlign: "center",
	},
	tileLeader: {
		borderColor: Colors.pickstop,
	},
	tilePlace: {
		fontSize: 11,
		letterSpacing: 1,
		textTransform: "uppercase",
		color: Colors.primaryDarker,
	},
	tilePlaceLeader: {
		color: Colors.pickstop,
	},
	tileAward: {
		height: 44,
		width: 44,
		display: "block",
		margin: "4px auto 6px",
	},
	tileScore: {
		marginTop: 6,
		fontSize: 16,
		fontWeight: "bold",
		color: Colors.pickstop,
	},
	scoreHeader: {
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
		cursor: "pointer",
	},
	scoreHeaderIcon: {
		fontSize: 14,
		color: Colors.pickstop,
	},
});

const bestAward = (record) => ({
	image: record.trophy1ImagePath,
	event: record.event1Name,
});

const awardStrip = (record) => (
	<span className="topTrophies">
		{[1, 2, 3, 4, 5].map((n) => {
			const src = record[`trophy${n}ImagePath`];
			if (!src) {
				return null;
			}
			return (
				<img
					key={n}
					className="awardImage"
					alt={record[`event${n}Name`] || "Trophy"}
					title={record[`event${n}Name`] || ""}
					src={src}
				/>
			);
		})}
	</span>
);

const bandFor = (rank, field) => {
	if (rank <= 10) {
		return "Top 10";
	}
	if (!field) {
		return null;
	}
	const percentile = (rank / field) * 100;
	if (percentile <= 10) {
		return "Top 10%";
	}
	if (percentile <= 30) {
		return "Top 30%";
	}
	if (percentile <= 50) {
		return "Top 50%";
	}
	return null;
};

export default function AwardLeaders(props) {
	const styles = useStyles();

	const [awardLeaderState, setAwardLeaderState] = React.useState({
		items: [],
		searchItems: null,
		totalRecords: 0,
		rowNumberToGoTo: 1,
		userIdToGoTo: null,
	});
	const [myRows, setMyRows] = React.useState([]);
	const [season, setSeason] = React.useState(null);

	React.useEffect(() => {
		TrophyService.getTrophyLeaderboard().then((leaderboardData) => {
			let copy = { ...awardLeaderState };
			copy.items = leaderboardData;
			setAwardLeaderState(copy);
		});
		TrophyService.getTrophyLeaderboardUserRow(UserManager.getUserId()).then(
			(data) => {
				setMyRows(data ?? []);
			}
		);
		SeasonService.getCurrent()
			.then((data) => setSeason(data))
			.catch(() => setSeason(null));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getLeaderboardData = (startRow, overwriteData, isUp) => {
		const def = $.Deferred();
		TrophyService.getTrophyLeaderboard(startRow).then((leaderboard) => {
			let items = [...awardLeaderState.items];
			let newItems = null;
			if (overwriteData) {
				newItems = leaderboard;
			} else {
				if (isUp) {
					newItems = leaderboard.concat(items);
				} else {
					newItems = items.concat(leaderboard);
				}
			}
			let copy = { ...awardLeaderState };
			copy.items = newItems;
			copy.searchItems = null;
			copy.rowNumberToGoTo = 1;
			copy.userIdToGoTo = null;
			setAwardLeaderState(copy);
			def.resolve(newItems.length > 0);
		});
		return def.promise();
	};

	const getLeaderboardAdditionalData = (fromTop) => {
		return TrophyService.getTrophyLeaderboardAdditional(fromTop).then(
			(info) => {
				let itemIdx = getItemIdx(UserManager.getUserId(), info.records);
				let copy = { ...awardLeaderState };
				copy.items = info.records;
				copy.searchItems = null;
				copy.totalRecords = info.totalRecords;
				copy.rowNumberToGoTo = fromTop || !itemIdx ? 1 : itemIdx;
				copy.userIdToGoTo = fromTop ? null : UserManager.getUserId();
				setAwardLeaderState(copy);
			}
		);
	};

	const searchLeaderboardData = (term) => {
		return TrophyService.searchTrophyLeaderboard(term).then((data) => {
			let copy = { ...awardLeaderState };
			copy.searchItems = data;
			copy.totalRecords = data.length;
			copy.rowNumberToGoTo = 1;
			copy.userIdToGoTo = null;
			setAwardLeaderState(copy);
		});
	};

	const getItemIdx = (id, items) => {
		if (!id || !items) {
			return null;
		}
		return items.findIndex((r) => {
			return r.userId === id;
		});
	};

	const nameCell = (record, autoWidth) => (
		<span className="name">
			<UserCard
				userId={record.userId}
				dontLoadData={true}
				autoWidth={autoWidth}
				userName={record.username}
				pinnedEventName={record.pinnedEventName}
				pinnedTrophyImagePath={record.pinnedTrophyImagePath}
				nameplateId={record.nameplateId}
			/>
		</span>
	);

	const cardRowJsx = (record) => {
		const best = bestAward(record);
		return (
			<>
				<span className="position">{record.rank}</span>
				{nameCell(record)}
				<span className="best">
					{best.image && (
						<img alt={best.event || "Award"} title={best.event || ""} src={best.image} />
					)}
				</span>
				<span className="ev">{best.event}</span>
				<span className="awardScore">{record.score ?? 0}</span>
			</>
		);
	};

	const modalRowJsx = (record) => (
		<>
			<span className="position">{record.rank}</span>
			{nameCell(record, true)}
			<span className="ev">{bestAward(record).event}</span>
			{awardStrip(record)}
			<span className="awardScore">{record.score ?? 0}</span>
		</>
	);

	const openScoreExplainer = () =>
		ModalHelper.openModal(<SeasonScoreModal season={season} />);

	const scoreHeader = (
		<span
			className={styles.scoreHeader}
			onClick={openScoreExplainer}
			title="How the season score works"
		>
			Pts
			<InfoOutlined className={styles.scoreHeaderIcon} />
		</span>
	);

	const items = awardLeaderState.items ?? [];
	const field = items.length > 0 ? items[0].totalRecords : 0;
	const me = myRows.find((r) => r.userId === UserManager.getUserId());

	const standingJsx = () => {
		if (!me) {
			return null;
		}
		const above = myRows.find((r) => r.rank === me.rank - 1);
		const below = myRows.find((r) => r.rank === me.rank + 1);
		const tiedAbove = above && above.score === me.score;
		const tiedBelow = below && below.score === me.score;
		const band = bandFor(me.rank, field);

		let gaps = [];
		if (tiedAbove && tiedBelow) {
			gaps.push(`Tied at ${me.score} pts`);
		} else {
			if (above) {
				gaps.push(
					tiedAbove
						? `Tied with #${above.rank}`
						: `${above.score - me.score} behind #${above.rank}`
				);
			}
			if (below) {
				gaps.push(
					tiedBelow
						? `Tied with #${below.rank}`
						: `${me.score - below.score} ahead of #${below.rank}`
				);
			}
		}

		return (
			<div className={styles.standing}>
				<div className={styles.standingTop}>
					<span className={styles.standingRank}>#{me.rank}</span>
					{field > 0 && (
						<span className={styles.standingField}>of {field}</span>
					)}
					{band && <span className={styles.standingBand}>{band}</span>}
					<span className={styles.standingScore}>{me.score ?? 0}</span>
					<span className={styles.standingUnit}>PTS</span>
				</div>
				{gaps.length > 0 && (
					<div className={styles.standingGap}>
						{gaps.map((gap, index) => (
							<React.Fragment key={gap}>
								{index > 0 && <span className={styles.gapSeparator}>|</span>}
								{gap}
							</React.Fragment>
						))}
					</div>
				)}
			</div>
		);
	};

	const podiumJsx = () => (
		<div className={styles.podium}>
			{items.slice(0, 3).map((record, index) => {
				const best = bestAward(record);
				return (
					<div
						key={record.userId}
						className={`${styles.tile} ${index === 0 ? styles.tileLeader : ""}`}
					>
						<div
							className={`${styles.tilePlace} ${
								index === 0 ? styles.tilePlaceLeader : ""
							}`}
						>
							{podiumPlaces[index]}
						</div>
						{best.image && (
							<img
								className={styles.tileAward}
								alt={best.event || "Award"}
								title={best.event || ""}
								src={best.image}
							/>
						)}
						{nameCell(record)}
						<div className={styles.tileScore}>{record.score ?? 0}</div>
					</div>
				);
			})}
		</div>
	);

	const seasonParts = [];
	if (season) {
		seasonParts.push(season.name);
		seasonParts.push(
			`${toMonthNameDayAndYear(season.startDate)} - ${toMonthNameDayAndYear(
				season.endDate
			)}`
		);
	}
	if (field > 0) {
		seasonParts.push(`${field} entrants`);
	}

	const headerJsx = (
		<div className={styles.headerRow}>
			<ContentHeader text="Award Leaders" />
			<span className={styles.season}>
				{seasonParts.map((part, index) => (
					<React.Fragment key={part}>
						{index > 0 && <span className={styles.seasonDot}>&middot;</span>}
						{part}
					</React.Fragment>
				))}
			</span>
		</div>
	);

	if (items.length < 1) {
		return (
			<Box className={styles.root}>
				{headerJsx}
				No leaderboard data to show.
			</Box>
		);
	}

	// The podium already shows the top three, so the table picks up at fourth.
	const tableDataAry = items.slice(3, 10).map((record) => cardRowJsx(record));

	// Search results only reach the modal, the card keeps the season list.
	const modalItems = awardLeaderState.searchItems ?? items;

	const modalDataAry = modalItems.map((record, index) => (
		<div
			data-id={record.userId}
			key={index}
			className={"leaderboard-row " + (record.rank === 1 ? "first" : "")}
		>
			{modalRowJsx(record)}
		</div>
	));

	return (
		<Box className={styles.root}>
			{headerJsx}
			{standingJsx()}
			{podiumJsx()}
			<Leaderboard
				leaderboardClass={`trophy`}
				loadData={getLeaderboardData}
				loadDataAddtl={getLeaderboardAdditionalData}
				totalRecords={awardLeaderState.totalRecords}
				leaderboardHeaders={[`#`, `Player`, ``, `Best result`, scoreHeader]}
				modalLeaderboardHeaders={[
					`#`,
					`Player`,
					`Best result`,
					`This season`,
					scoreHeader,
				]}
				modalTitle={
					season ? `Award Leaders - ${season.name}` : `Award Leaders`
				}
				moreLabel={
					<>
						{field > 0 ? `View all ${field}` : `View all`}
						<ChevronRight />
					</>
				}
				tableData={tableDataAry}
				modalTableData={modalDataAry}
				eventData={null}
				hasBracket={true}
				noDataEl={null}
				firstRow={modalItems.length > 0 ? modalItems[0].rank : 0}
				lastRow={
					modalItems.length > 0 ? modalItems[modalItems.length - 1].rank : 0
				}
				rowNumberToGoTo={awardLeaderState.rowNumberToGoTo}
				userIdToGoTo={awardLeaderState.userIdToGoTo}
				searchLeaderboard={searchLeaderboardData}
				totalEntrants={field}
				currentUserRow={null}
				hideSubmissions={true}
			/>
		</Box>
	);
}
